import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import { Plus, Save, TrendingUp, BarChart3, Filter, Target } from 'lucide-react';
import { useGoals } from '../hooks/useGoals';
import { formatDate, createProgressEntry, DATA_TYPES } from '../utils/dataModels';

const ProgressTracker = ({ goals, students, onNavigate, announce }) => {
  const { addProgressEntry } = useGoals();
  const [selectedStudentId, setSelectedStudentId] = useState('all');
  const [selectedGoalId, setSelectedGoalId] = useState('');
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'individual'
  const [progressData, setProgressData] = useState({
    date: formatDate(new Date(), 'YYYY-MM-DD'),
    value: '',
    notes: '',
    dataType: 'Percentage'
  });

  // Filter goals based on selected student
  const filteredGoals = useMemo(() => {
    if (selectedStudentId === 'all') {
      return goals;
    }
    return goals.filter(goal => goal.studentId === selectedStudentId);
  }, [goals, selectedStudentId]);

  // Get the selected goal for individual view
  const selectedGoal = useMemo(() => {
    return goals.find(goal => goal.id === selectedGoalId);
  }, [goals, selectedGoalId]);

  // Prepare chart data for selected goal
  const chartData = useMemo(() => {
    if (!selectedGoal) return [];
    
    return selectedGoal.progressData.map(entry => ({
      date: formatDate(new Date(entry.date), 'MM/DD'),
      value: entry.value,
      fullDate: entry.date,
      notes: entry.notes,
      dataType: entry.dataType
    })).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [selectedGoal]);

  // Calculate target line value (extract number from target criteria)
  const targetValue = useMemo(() => {
    if (!selectedGoal || !selectedGoal.targetCriteria) return null;
    
    const match = selectedGoal.targetCriteria.match(/(\d+)%/);
    return match ? parseInt(match[1]) : null;
  }, [selectedGoal]);

  const handleProgressSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedGoalId || !progressData.value) {
      announce('Please select a goal and enter a progress value', 'assertive');
      return;
    }

    const entry = createProgressEntry({
      ...progressData,
      value: parseFloat(progressData.value)
    });

    const success = addProgressEntry(selectedGoalId, entry);
    if (success) {
      announce('Progress entry added successfully');
      setProgressData({
        date: formatDate(new Date(), 'YYYY-MM-DD'),
        value: '',
        notes: '',
        dataType: 'Percentage'
      });
      setShowProgressForm(false);
    }
  };

  const getProgressTrend = (goal) => {
    if (!goal.progressData || goal.progressData.length < 2) return 'no-trend';
    
    const recent = goal.progressData.slice(-2);
    const current = recent[1].value;
    const previous = recent[0].value;
    
    if (current > previous) return 'improving';
    if (current < previous) return 'declining';
    return 'stable';
  };

  const getTrendColor = (trend) => {
    const colors = {
      'improving': 'text-green-600',
      'declining': 'text-red-600',
      'stable': 'text-yellow-600',
      'no-trend': 'text-gray-600'
    };
    return colors[trend];
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return '↗';
      case 'declining': return '↘';
      case 'stable': return '→';
      default: return '—';
    }
  };

  const ProgressForm = () => (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Progress Entry</h3>
      
      <form onSubmit={handleProgressSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="goal-select" className="block text-sm font-medium text-gray-900 mb-2">
              Select Goal <span className="text-red-500">*</span>
            </label>
            <select
              id="goal-select"
              value={selectedGoalId}
              onChange={(e) => setSelectedGoalId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="">Choose a goal...</option>
              {filteredGoals.map(goal => {
                const student = students.find(s => s.id === goal.studentId);
                return (
                  <option key={goal.id} value={goal.id}>
                    {student?.firstName} {student?.lastName} - {goal.description.substring(0, 50)}...
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label htmlFor="progress-date" className="block text-sm font-medium text-gray-900 mb-2">
              Date
            </label>
            <input
              type="date"
              id="progress-date"
              value={progressData.date}
              onChange={(e) => setProgressData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="progress-value" className="block text-sm font-medium text-gray-900 mb-2">
              Progress Value <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="progress-value"
              min="0"
              max="100"
              step="0.1"
              value={progressData.value}
              onChange={(e) => setProgressData(prev => ({ ...prev, value: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter value (0-100)"
              required
            />
          </div>

          <div>
            <label htmlFor="data-type" className="block text-sm font-medium text-gray-900 mb-2">
              Data Type
            </label>
            <select
              id="data-type"
              value={progressData.dataType}
              onChange={(e) => setProgressData(prev => ({ ...prev, dataType: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {DATA_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="progress-notes" className="block text-sm font-medium text-gray-900 mb-2">
            Notes (Optional)
          </label>
          <textarea
            id="progress-notes"
            value={progressData.notes}
            onChange={(e) => setProgressData(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Add any observations or notes about this progress entry..."
          />
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => setShowProgressForm(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <Save size={16} className="mr-2" />
            Save Progress
          </button>
        </div>
      </form>
    </div>
  );

  const GoalProgressCard = ({ goal }) => {
    const student = students.find(s => s.id === goal.studentId);
    const trend = getProgressTrend(goal);
    const latestProgress = goal.progressData[goal.progressData.length - 1];

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-gray-900 mb-1">
              {student?.firstName} {student?.lastName}
            </h4>
            <p className="text-sm text-gray-900 mb-2">{goal.description}</p>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {goal.domain}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                goal.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                goal.status === 'Mastered' ? 'bg-green-100 text-green-800' :
                goal.status === 'Not Started' ? 'bg-gray-100 text-gray-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {goal.status}
              </span>
            </div>
          </div>
          
          <div className="text-right">
            <div className={`text-2xl font-bold ${getTrendColor(trend)}`}>
              {latestProgress ? `${latestProgress.value}%` : 'No data'}
            </div>
            <div className={`text-sm ${getTrendColor(trend)} flex items-center`}>
              <span className="mr-1">{getTrendIcon(trend)}</span>
              {trend.replace('-', ' ')}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm text-gray-900 mb-4">
          <div>
            <span className="font-medium">Entries:</span> {goal.progressData.length}
          </div>
          <div>
            <span className="font-medium">Target:</span> {goal.targetCriteria || 'Not set'}
          </div>
          <div>
            <span className="font-medium">Last Update:</span> {
              latestProgress ? formatDate(latestProgress.date) : 'Never'
            }
          </div>
          <div>
            <span className="font-medium">Review:</span> {goal.reviewFrequency}
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedGoalId(goal.id);
              setViewMode('individual');
            }}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <BarChart3 size={16} className="mr-2" />
            View Chart
          </button>
          <button
            onClick={() => {
              setSelectedGoalId(goal.id);
              setShowProgressForm(true);
            }}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <Plus size={16} className="mr-2" />
            Add Data
          </button>
        </div>
      </div>
    );
  };

  const IndividualGoalView = () => {
    if (!selectedGoal) return null;
    
    const student = students.find(s => s.id === selectedGoal.studentId);

    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Progress Chart - {student?.firstName} {student?.lastName}
              </h3>
              <p className="text-gray-900">{selectedGoal.description}</p>
              <div className="flex items-center space-x-2 mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {selectedGoal.domain}
                </span>
                <span className="text-sm text-gray-900">
                  Target: {selectedGoal.targetCriteria || 'Not specified'}
                </span>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('overview')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Back to Overview
              </button>
              <button
                onClick={() => {
                  setSelectedGoalId(selectedGoal.id);
                  setShowProgressForm(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Plus size={16} className="mr-2" />
                Add Data Point
              </button>
            </div>
          </div>

          {chartData.length > 0 ? (
            <div>
              <div 
                role="img" 
                aria-labelledby="chart-title"
                aria-describedby="chart-desc"
                className="h-64 w-full"
              >
                <h4 id="chart-title" className="sr-only">
                  Progress Chart for {selectedGoal.description}
                </h4>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      aria-label="Date axis"
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 12 }}
                      aria-label="Progress percentage axis"
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, 'Progress']}
                      labelFormatter={(label) => `Date: ${label}`}
                      content={({ active, payload, label }) => {
                        if (active && payload && payload[0]) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 border border-gray-200 rounded shadow">
                              <p className="font-medium">{`Date: ${label}`}</p>
                              <p className="text-primary">{`Progress: ${data.value}%`}</p>
                              <p className="text-sm text-gray-600">{`Type: ${data.dataType}`}</p>
                              {data.notes && (
                                <p className="text-sm text-gray-600 mt-1">{`Notes: ${data.notes}`}</p>
                              )}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {targetValue && (
                      <ReferenceLine 
                        y={targetValue} 
                        stroke="#ef4444" 
                        strokeDasharray="5 5"
                        label={{ value: `Target: ${targetValue}%`, position: "topRight" }}
                      />
                    )}
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="var(--accent-color)" 
                      strokeWidth={3}
                      dot={{ r: 6, fill: "var(--accent-color)" }}
                      aria-label="Progress trend line"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div id="chart-desc" className="sr-only">
                Chart showing progress from {chartData[0]?.fullDate} to {chartData[chartData.length - 1]?.fullDate}. 
                Current progress: {chartData[chartData.length - 1]?.value}%. 
                Target: {selectedGoal.targetCriteria}.
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <TrendingUp size={48} className="mx-auto text-gray-300 mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No progress data yet</h4>
              <p className="text-gray-900 mb-4">Add your first progress entry to see the chart</p>
              <button
                onClick={() => setShowProgressForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Plus size={16} className="mr-2" />
                Add First Entry
              </button>
            </div>
          )}
        </div>

        {/* Progress History Table */}
        {chartData.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Progress History</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedGoal.progressData.slice().reverse().map((entry, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(entry.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.value}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.dataType}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {entry.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Progress Tracker</h1>
          <p className="text-gray-900 mt-1">
            Monitor and record IEP goal progress
          </p>
        </div>
        
        <button
          onClick={() => setShowProgressForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <Plus size={16} className="mr-2" />
          Add Progress
        </button>
      </div>

      {/* Student Filter */}
      {viewMode === 'overview' && (
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label htmlFor="student-filter" className="block text-sm font-medium text-gray-900 mb-1">
                <Filter size={16} className="inline mr-1" />
                Filter by Student
              </label>
              <select
                id="student-filter"
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Students</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.firstName} {student.lastName}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="text-sm text-gray-900">
              Showing {filteredGoals.length} goal{filteredGoals.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {/* Progress Form */}
      {showProgressForm && <ProgressForm />}

      {/* Main Content */}
      {viewMode === 'individual' ? (
        <IndividualGoalView />
      ) : (
        <div className="space-y-6">
          {filteredGoals.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <Target size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No goals found</h3>
              <p className="text-gray-900 mb-4">
                {selectedStudentId !== 'all' 
                  ? 'This student has no goals yet.' 
                  : 'Create some goals first to track progress.'}
              </p>
              <button
                onClick={() => onNavigate('goals')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Plus size={16} className="mr-2" />
                Create Goals
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredGoals.map(goal => (
                <GoalProgressCard key={goal.id} goal={goal} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;